import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Truck, PackageOpen, MapPin, CalendarDays, FileText, User, Phone, Mail, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    service_type: '',
    departure_address: '',
    arrival_address: '',
    desired_date: '',
    desired_time: '',
    details: '',
    full_name: '',
    phone: '',
    email: '',
  });

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = () => {
    if (step === 1 && !form.service_type) {
      setError('Veuillez sélectionner un type de service.');
      return false;
    }
    if (step === 2) {
      if (!form.departure_address || !form.arrival_address || !form.desired_date || !form.desired_time) {
        setError('Veuillez remplir tous les champs obligatoires.');
        return false;
      }
    }
    if (step === 3) {
      if (!form.full_name || !form.phone || !form.email) {
        setError('Veuillez remplir tous les champs obligatoires.');
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setError('Veuillez entrer une adresse email valide.');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, 3));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
    } catch (err) {
      setError("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const progressWidth = `${((step) / 3) * 100}%`;

  if (submitted) {
    return (
      <section id="devis" className="py-24 md:py-32 bg-[#111111]" data-testid="quote-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <CheckCircle className="w-20 h-20 text-[#10B981] mx-auto mb-6" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-['Oswald'] text-3xl sm:text-4xl font-bold uppercase text-white mb-4"
            data-testid="quote-success-title"
          >
            Demande envoyée !
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/60 text-lg"
          >
            Merci pour votre confiance. Nous vous répondrons dans les 24 heures.
          </motion.p>
        </div>
      </section>
    );
  }

  return (
    <section id="devis" ref={ref} className="py-24 md:py-32 bg-[#111111]" data-testid="quote-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-1 bg-[#FF0000] mx-auto mb-6" />
          <h2 className="font-['Oswald'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white" data-testid="quote-form-title">
            Demandez votre devis gratuit
          </h2>
          <p className="text-white/50 mt-4 text-base md:text-lg">
            Réponse sous 24h
          </p>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mb-10"
        >
          <div className="flex justify-between mb-3">
            {['Service', 'Détails', 'Coordonnées'].map((label, i) => (
              <span
                key={label}
                className={`text-xs uppercase tracking-wider font-medium ${
                  step > i ? 'text-[#FF0000]' : step === i + 1 ? 'text-white' : 'text-white/50'
                }`}
              >
                {label}
              </span>
            ))}
          </div>
          <div className="h-1 bg-[#2A2A2A] w-full">
            <div
              className="h-full bg-[#FF0000] progress-bar-fill"
              style={{ width: progressWidth }}
              data-testid="progress-bar"
            />
          </div>
        </motion.div>

        {/* Form card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="bg-[#0A0A0A] border border-[#2A2A2A] p-6 md:p-10"
        >
          <AnimatePresence mode="wait">
            {/* Step 1 */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="form-step"
                data-testid="form-step-1"
              >
                <h3 className="font-['Oswald'] text-xl font-bold uppercase text-white mb-6">
                  Quel service vous intéresse ?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => update('service_type', 'Transport de marchandises')}
                    className={`radio-card border p-6 text-left flex flex-col items-center gap-4 ${
                      form.service_type === 'Transport de marchandises'
                        ? 'active border-[#FF0000] bg-[#FF0000]/5'
                        : 'border-[#2A2A2A] hover:border-[#FF0000]/50'
                    }`}
                    data-testid="service-type-transport"
                  >
                    <Truck className={`w-10 h-10 ${form.service_type === 'Transport de marchandises' ? 'text-[#FF0000]' : 'text-white/50'}`} />
                    <span className="font-['Oswald'] text-lg font-bold uppercase text-white">Transport</span>
                    <span className="text-white/50 text-sm text-center">Marchandises & colis</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => update('service_type', 'Déménagement')}
                    className={`radio-card border p-6 text-left flex flex-col items-center gap-4 ${
                      form.service_type === 'Déménagement'
                        ? 'active border-[#FF0000] bg-[#FF0000]/5'
                        : 'border-[#2A2A2A] hover:border-[#FF0000]/50'
                    }`}
                    data-testid="service-type-demenagement"
                  >
                    <PackageOpen className={`w-10 h-10 ${form.service_type === 'Déménagement' ? 'text-[#FF0000]' : 'text-white/50'}`} />
                    <span className="font-['Oswald'] text-lg font-bold uppercase text-white">Déménagement</span>
                    <span className="text-white/50 text-sm text-center">Particuliers & pros</span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="form-step space-y-5"
                data-testid="form-step-2"
              >
                <h3 className="font-['Oswald'] text-xl font-bold uppercase text-white mb-2">
                  Détails de votre demande
                </h3>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <MapPin className="w-4 h-4" /> Adresse de départ *
                  </label>
                  <input
                    type="text"
                    value={form.departure_address}
                    onChange={(e) => update('departure_address', e.target.value)}
                    placeholder="Ex: Rue de Lausanne 12, 1000 Lausanne"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-departure"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <MapPin className="w-4 h-4" /> Adresse d'arrivée *
                  </label>
                  <input
                    type="text"
                    value={form.arrival_address}
                    onChange={(e) => update('arrival_address', e.target.value)}
                    placeholder="Ex: Avenue de la Gare 5, 1400 Yverdon"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-arrival"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <CalendarDays className="w-4 h-4" /> Date souhaitée *
                  </label>
                  <input
                    type="date"
                    value={form.desired_date}
                    onChange={(e) => update('desired_date', e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-date"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <CalendarDays className="w-4 h-4" /> Heure souhaitée *
                  </label>
                  <input
                    type="time"
                    value={form.desired_time}
                    onChange={(e) => update('desired_time', e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-time"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <FileText className="w-4 h-4" /> Détails supplémentaires
                  </label>
                  <textarea
                    value={form.details}
                    onChange={(e) => update('details', e.target.value)}
                    placeholder="Décrivez votre besoin (volume, objets fragiles, étage, etc.)"
                    rows={4}
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 resize-none transition-colors"
                    data-testid="input-details"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="form-step space-y-5"
                data-testid="form-step-3"
              >
                <h3 className="font-['Oswald'] text-xl font-bold uppercase text-white mb-2">
                  Vos coordonnées
                </h3>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <User className="w-4 h-4" /> Nom et prénom *
                  </label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => update('full_name', e.target.value)}
                    placeholder="Jean Dupont"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-fullname"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <Phone className="w-4 h-4" /> Téléphone *
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="+41 78 000 00 00"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white/70 text-sm mb-2 uppercase tracking-wider">
                    <Mail className="w-4 h-4" /> Email *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="votre@email.ch"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-4 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] outline-none placeholder:text-gray-600 transition-colors"
                    data-testid="input-email"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-[#EF4444] text-sm mt-4"
              data-testid="form-error"
            >
              {error}
            </motion.p>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 gap-4">
            {step > 1 ? (
              <button
                onClick={prevStep}
                className="flex items-center gap-2 border border-white/20 hover:bg-white/10 text-white px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all"
                data-testid="form-btn-prev"
              >
                <ArrowLeft className="w-4 h-4" /> Retour
              </button>
            ) : (
              <div />
            )}
            {step < 3 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 bg-[#FF0000] hover:bg-[#D90000] text-white px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all hover:scale-105 ml-auto"
                data-testid="form-btn-next"
              >
                Suivant <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 bg-[#FF0000] hover:bg-[#D90000] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all hover:scale-105 ml-auto disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
                data-testid="form-btn-submit"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
